import { AxiosPromise } from "node_modules/axios";
import React from "react";
import SectionPanel from "../components/panels/SectionPanel/SectionPanel";
import { SectionTemplateHeader } from "../components/MainSectionTemplate/SectionTemplateHeader";
import { InPageEditableSectionField } from "../components/MainSectionTemplate/types";
import InPageEditableFieldWithLabel from "../components/MainSectionTemplate/InPageEditableFieldWithLabel";
import { useMutation } from "@tanstack/react-query";
import { useCustomQuery } from "utilities/hookutils";
import { ButtonSecondary } from "../components/Atoms/Button";
import { newLineToBr } from "utilities";
import { Loading } from "../components/panels";
import PanelSegment from "components/panels/PanelSegment/PanelSegment";
import { SomethingUnexpectedHappenedMessageBox } from "components/MessageBox/SomethingUnexpectedHappenedMessageBox";

interface Autolink {
  type: "autolink";
  link: (id: number) => AxiosPromise<server.dto.ApiResponse>;
  unlink: () => AxiosPromise<server.dto.ApiResponse>;
}

interface ManualLink {
  type: "manual";
  value: number;
  onChange: (id: number) => void;
}

interface Props<T> {
  initialHubSpotId: number;
  urlToHubSpotEntity: (id: number) => string;
  title: string;
  apiKey: string;
  collapsible?: {
    start: "open" | "closed"
    uniqueRowId: string
    persist: boolean
  }
  fetch: (id: number) => AxiosPromise<server.dto.ApiResponseWrapper<T>>
  link: Autolink | ManualLink
  // link: (id: number) => AxiosPromise<server.dto.ApiResponse>
  // unlink: () => AxiosPromise<server.dto.ApiResponse>
  renderPicker: (onLink: (id: number) => void) => React.ReactNode
  dataFields: (item: T) => InPageEditableSectionField[]
  create?: {
    api: () => AxiosPromise<server.dto.ApiResponseWrapper<number>>
    createButtonLabel: string
  }
  panelRef?: React.RefObject<HTMLDivElement>
}

const isAutoLink = (link: Autolink | ManualLink): link is Autolink => {
  return link.type === "autolink";
}

const displayErrors = (errors: string[]) => {
  window.displayGenericModal("Error", errors.join("\n"), "tiny", "red");
}

const makeApiCall = async <T extends unknown>(
  api: () => AxiosPromise<server.dto.ApiResponse | server.dto.ApiResponseWrapper<T>>,
  onSuccess: (response?: T) => void
) => {
  try {
    const res = await api();
    if (res.data.hasErrors) {
      const errors = res.data.errors.map(e => e.message);
      window.displayGenericModal("Error", errors.join("\n"), "tiny", "red");
    } else {
      onSuccess("data" in res.data ? res.data.data : undefined);
    }
  } catch (e) {
    console.error(e);
    displayErrors(["Exception while trying to make an API call", e.message]);
  }
}

const GenericHubSpotPanel = <T extends unknown>(props: Props<T>) => {
  const [hsId, setHsId] = React.useState(props.initialHubSpotId);
  const id = !isAutoLink(props.link) ? props.link.value : hsId;
  const setId = (id: number) => {
    if (!isAutoLink(props.link)) {
      props.link.onChange(id);
    } else {
      setHsId(id);
    }
  }

  const hubSpotApi = useCustomQuery({
    queryFn: async () => props.fetch(id),
    queryKey: [props.apiKey, "fetch", id],
    retry: false,
    enabled: id != null
  });


  const createMutation = props.create ? useMutation(props.create.api) : undefined;


  const linkMutation = isAutoLink(props.link) ? useMutation(props.link.link) : undefined;
  const unlinkMutation = isAutoLink(props.link) ? useMutation(props.link.unlink) : undefined;

  const onUnlink = () => {
    if (!isAutoLink(props.link)) {
      setId(null);
      return;
    }
    window.displayDeleteConfirmationModal({
      title: "Unlink",
      onDeleteHandler: async () => {
        makeApiCall(
          unlinkMutation.mutateAsync,
          () => setId(null)
        )
      }
    })
  }

  const onLink = (hsId: number) => {
    if (!isAutoLink(props.link)) {
      setId(hsId);
      return;
    }

    makeApiCall(
      () => linkMutation.mutateAsync(hsId),
      () => setId(hsId)
    )
  }

  const onCreate = () => {
    makeApiCall(
      createMutation.mutateAsync,
      (id) => setId(id)
    )
  }

  if (id == null) {
    return <SectionPanel.Panel
      panelRef={props.panelRef}
      collapsible={props.collapsible}
      header={<>
        <PanelSegment.Title title={props.title} />
        {(props.create && isAutoLink(props.link)) && <PanelSegment.Button onClick={onCreate} label={props.create.createButtonLabel} type="secondary" />}
      </>}>
      <div className="main-section-template-grid">
        <InPageEditableFieldWithLabel
          compact={false}
          field={{
            key: "hubSpotId",
            label: "HubSpot ID",
            render: () => <div>{props.renderPicker(onLink)}</div>
          }}
        />

      </div>
    </SectionPanel.Panel>
  }

  const hasLoaded = hubSpotApi.data?.data.data != null;
  const isLoading = id != null && hubSpotApi.isLoading;;
  const hasUnknownError = hubSpotApi.isError;
  const hasServerErrors = hubSpotApi.data?.data.hasErrors;

  return <SectionPanel.Panel
    collapsible={props.collapsible}
    header={<SectionTemplateHeader>{props.title}</SectionTemplateHeader>}
    panelRef={props.panelRef}
  >
    <div className="main-section-template-grid">
      <InPageEditableFieldWithLabel
        compact={false}
        field={{
          key: "hubSpotId",
          label: "HubSpot ID",
          render: () => <div>{id}</div>

        }}
      />
      {isLoading && <Loading />}

      {hasLoaded && props.dataFields(hubSpotApi.data.data.data).map((field) => (
        <InPageEditableFieldWithLabel
          key={field.key}
          compact={false}
          field={field}
        />
      ))}

      {hasUnknownError && <InPageEditableFieldWithLabel
        compact={false}
        field={{
          key: "unknownError",
          label: "Error",
          render: () => <SomethingUnexpectedHappenedMessageBox />,
        }}
      />}

      {hasServerErrors && <InPageEditableFieldWithLabel
        compact={false}
        field={{
          key: "hsError",
          label: "Error",
          render: () => <div className="ui message red">
            <div>{newLineToBr(hubSpotApi.data.data.errors.map(e => e.message).join("\n"))}</div>
          </div>,
        }}
      />}

      <InPageEditableFieldWithLabel
        compact={false}
        field={{
          key: "hubspotUnlink",
          label: "",
          render: () => <div className="flex align-center gap-large">
            <div className="grow1"></div>
            <div>
              <a href={props.urlToHubSpotEntity(id)} target="_blank" rel="noreferrer noopener">View in HubSpot</a>
            </div>
            <div>
              <ButtonSecondary loading={unlinkMutation?.isLoading ?? false} label="Unlink" onClick={() => { onUnlink(); }} />
            </div>
          </div>,
        }}
      />
    </div>
  </SectionPanel.Panel>
}

export default GenericHubSpotPanel;